import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ResourceStateControl from "../ResourceStateControl";
import { useResourceDomainContext } from "../../ResourceDomainProvider";
import {
  getId,
  getOperationItems,
  readValue,
  requireSuccessfulServiceResponse
} from "../wizard/resourceConfigWizardUtils";
import {
  getDestinationDeployments,
  getDestinationDefaultsFromRecord,
  toDestinationSetupDefaults,
  withDeploymentConnectionCheckId
} from "../../workspace/resourceWorkspace.destinationUtils";
import {
  getDestinationDeploymentRows,
  getDeploymentRecordId,
  getDestinationManagerKey,
  getEntityDeploymentRows,
  getEntityObjectDisplayName,
  loadDestinationById
} from "../wizard/steps/destinationSetupStepUtils";
import DestinationScheduleControl from "./DestinationScheduleControl";

const DEFAULT_DESTINATION_RELATIONSHIP = "SchemaDestination";

const parseDefaults = (productResource) => {
  const raw = readValue(productResource, "SetupDefaultsJson", "setupDefaultsJson");
  if (!raw || typeof raw !== "string") return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const hasExplicitDestinationDefaults = (defaults = {}) =>
  Boolean(
    readValue(defaults, "destinationItemName", "DestinationItemName", "itemName", "ItemName")
    || readValue(defaults, "managerKey", "ManagerKey")
    || readValue(defaults, "deploymentCollection", "DeploymentCollection", "deploymentCollectionField", "DeploymentCollectionField")
  );

const normalizeCompare = (value) => String(value ?? "").trim().toLowerCase();

const getLinkedDestinationRecord = (configuration, defaults = {}) => {
  const itemName = readValue(defaults, "destinationItemName", "DestinationItemName") || "";
  const relationship = readValue(defaults, "destinationRelationship", "DestinationRelationship") || DEFAULT_DESTINATION_RELATIONSHIP;
  const linkedRecords = readValue(configuration, "LinkedRecords", "linkedRecords") || [];
  return linkedRecords.find((record) =>
    normalizeCompare(readValue(record, "ItemName", "itemName")) === normalizeCompare(itemName)
    && normalizeCompare(readValue(record, "Relationship", "relationship")) === normalizeCompare(relationship)
  ) || linkedRecords.find((record) =>
    ["warehouse", "filestorage", "externalsystem"].includes(normalizeCompare(readValue(record, "ItemName", "itemName")))
    && normalizeCompare(readValue(record, "Relationship", "relationship")) === normalizeCompare(DEFAULT_DESTINATION_RELATIONSHIP)
  ) || null;
};

const getLinkedRecordItemId = (record) =>
  readValue(record, "ItemId", "itemId", "RecordId", "recordId", "_id", "id") || "";

const splitScheduleQueryIds = (values = []) =>
  values
    .flatMap((value) => String(value || "").split(","))
    .map((value) => value.trim())
    .filter(Boolean);

const getDeploymentIdFromScopeKey = (scopeKey) => {
  const match = /^deployment:([^|]+)/i.exec(String(scopeKey || ""));
  return match?.[1] || "";
};

const readScheduleRouteFilters = (search = "") => {
  const params = new URLSearchParams(search || "");
  const entityObjectIds = splitScheduleQueryIds([
    ...params.getAll("entityObjectIds"),
    ...params.getAll("entityObjectId")
  ]);
  return {
    deploymentId: params.get("deploymentId") || "",
    entityObjectIds,
    scopeKey: params.get("scopeKey") || ""
  };
};

const ResourceConfigurationDestinationScheduleScreen = ({
  selectedResource,
  configuration,
  environmentKey = "",
  clientProductOffers = [],
  onManagementActionsChange
}) => {
  const location = useLocation();
  const resourceDomainContext = useResourceDomainContext();
  const coreState = resourceDomainContext?.coreState;
  const scheduleRouteFilters = useMemo(() => readScheduleRouteFilters(location.search), [location.search]);
  const configuredDefaults = useMemo(() => parseDefaults(selectedResource?.productResource), [selectedResource?.productResource]);
  const destinationRecord = useMemo(() => getLinkedDestinationRecord(configuration, configuredDefaults), [configuration, configuredDefaults]);
  const defaults = useMemo(() => {
    if (hasExplicitDestinationDefaults(configuredDefaults)) return configuredDefaults;
    const fallbackDefaults = getDestinationDefaultsFromRecord(destinationRecord, selectedResource?.productResource);
    return fallbackDefaults ? toDestinationSetupDefaults(fallbackDefaults) : {};
  }, [configuredDefaults, destinationRecord, selectedResource?.productResource]);
  const managerKey = getDestinationManagerKey(defaults);
  const destinationMgr = resourceDomainContext?.managers?.[managerKey];
  const scheduleMgr = resourceDomainContext?.managers?.destinationSchedule;
  const eventMgr = resourceDomainContext?.managers?.event;
  const entityObjectMgr = resourceDomainContext?.managers?.entityObject;
  const destinationId = getLinkedRecordItemId(destinationRecord);
  const clientResourceId = selectedResource?.id || readValue(configuration, "ClientResourceId", "clientResourceId") || "";
  const resourceConfigurationId = getId(configuration);
  const [state, setState] = useState({
    destination: null,
    deployments: [],
    entityDeployments: [],
    entityObjects: [],
    loading: Boolean(destinationId),
    error: ""
  });
  const loadKeyRef = useRef("");
  const refreshKeyRef = useRef(0);

  const load = useCallback(async () => {
    const loadKey = `${managerKey}:${destinationId || "none"}:${refreshKeyRef.current}`;
    if (!destinationId) {
      setState({
        destination: null,
        deployments: [],
        entityDeployments: [],
        entityObjects: [],
        loading: false,
        error: "No destination is linked to this Resource Configuration."
      });
      return;
    }
    if (!destinationMgr) {
      setState((previous) => ({ ...previous, loading: false, error: "Destination service is not available." }));
      return;
    }
    if (!entityObjectMgr?.Query) {
      setState((previous) => ({ ...previous, loading: false, error: "Entity Object service is not available." }));
      return;
    }
    if (loadKeyRef.current === loadKey) return;
    loadKeyRef.current = loadKey;
    setState((previous) => ({ ...previous, loading: true, error: "" }));
    try {
      const destination = await loadDestinationById({
        manager: destinationMgr,
        destinationId,
        coreState,
        fallbackMessage: "Destination could not be loaded."
      });
      if (!destination) throw new Error("Destination could not be loaded.");
      const activeSchemaVersionId = readValue(destination, "ActiveSchemaVersionId", "activeSchemaVersionId") || "";
      const deploymentRows = getDestinationDeploymentRows(destination, defaults);
      const entityObjectResult = activeSchemaVersionId
        ? await entityObjectMgr.Query({
          model: "x => x.IsDeleted == false && x.SchemaVersionId == schemaVersionId",
          params: { schemaVersionId: activeSchemaVersionId }
        })
        : { Items: [] };
      requireSuccessfulServiceResponse(entityObjectResult, "Entity Objects could not be loaded.", coreState);
      const normalizedDeployments = (deploymentRows.length ? deploymentRows : getDestinationDeployments(destination, defaults))
        .map(withDeploymentConnectionCheckId);
      setState({
        destination,
        deployments: normalizedDeployments,
        entityDeployments: getEntityDeploymentRows(destination),
        entityObjects: getOperationItems(entityObjectResult)
          .filter((entityObject) => readValue(entityObject, "IsDeleted", "isDeleted") !== true)
          .sort((left, right) => getEntityObjectDisplayName(left).localeCompare(getEntityObjectDisplayName(right))),
        loading: false,
        error: ""
      });
    } catch (ex) {
      setState((previous) => ({
        ...previous,
        loading: false,
        error: ex?.message || "Destination Schedule management could not be loaded."
      }));
    }
  }, [coreState, defaults, destinationId, destinationMgr, entityObjectMgr, managerKey]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(() => {
    refreshKeyRef.current += 1;
    loadKeyRef.current = "";
    void load();
  }, [load]);

  if (state.loading) {
    return <ResourceStateControl loading loadingText="Loading Destination Schedule..." />;
  }

  if (state.error && !state.destination) {
    return <ResourceStateControl tone="warning" title="Schedule unavailable" message={state.error} />;
  }

  const routeDeploymentId = scheduleRouteFilters.deploymentId || getDeploymentIdFromScopeKey(scheduleRouteFilters.scopeKey);
  if (!routeDeploymentId || !scheduleRouteFilters.scopeKey) {
    return (
      <ResourceStateControl
        tone="warning"
        title="Schedule link unavailable"
        message="Open Schedule from a deployment or Entity Scope row so the deployment and scope are provided."
      />
    );
  }

  const routeDeploymentExists = state.deployments.some((deployment) =>
    normalizeCompare(getDeploymentRecordId(deployment)) === normalizeCompare(routeDeploymentId)
  );
  if (!routeDeploymentExists) {
    return (
      <ResourceStateControl
        tone="warning"
        title="Schedule link unavailable"
        message="The linked deployment for this Schedule route could not be found."
      />
    );
  }

  return (
    <DestinationScheduleControl
      mode="management"
      destination={state.destination}
      deployments={state.deployments}
      entityObjects={state.entityObjects}
      entityDeployments={state.entityDeployments}
      defaults={defaults}
      clientResourceId={clientResourceId}
      resourceConfigurationId={resourceConfigurationId}
      environmentKey={environmentKey}
      clientProductOffers={clientProductOffers}
      scheduleManager={scheduleMgr}
      eventManager={eventMgr}
      initialDeploymentId={scheduleRouteFilters.deploymentId}
      initialEntityObjectIds={scheduleRouteFilters.entityObjectIds}
      initialScopeKey={scheduleRouteFilters.scopeKey}
      onRefresh={refresh}
      showManagementActions={false}
      onManagementActionsChange={onManagementActionsChange}
    />
  );
};

export default ResourceConfigurationDestinationScheduleScreen;
